import { useId, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { DSCard, DSTypography } from '@eiv2/design-system';

export type MetricTone = 'default' | 'success' | 'warning' | 'error';

export interface MetricCardProps {
  label: string;
  value: string;
  delta?: string;
  tone?: MetricTone;
  icon: ReactNode;
  /** Explicação exibida ao passar o mouse. */
  tooltip?: string;
}

const toneIconClass: Record<MetricTone, string> = {
  default: 'bg-brand-primary-3/10 text-brand-primary-3',
  success: 'bg-feedback-success/15 text-feedback-success',
  warning: 'bg-brand-primary-3-20 text-brand-primary-3',
  error: 'bg-feedback-danger/15 text-feedback-danger'
};

const toneDeltaClass: Record<MetricTone, string> = {
  default: 'text-gray-5',
  success: 'text-feedback-success',
  warning: 'text-brand-primary-3',
  error: 'text-feedback-danger'
};

type TooltipPosition = {
  left: number;
  top: number;
  placement: 'above' | 'below';
};

export function MetricCard({
  label,
  value,
  delta,
  tone = 'default',
  icon,
  tooltip
}: MetricCardProps) {
  const tooltipId = useId();
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition | null>(null);

  function showTooltip(target: HTMLElement) {
    if (!tooltip) return;

    const rect = target.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const placement: TooltipPosition['placement'] = spaceBelow < 96 ? 'above' : 'below';

    setTooltipPosition({
      left: rect.left + rect.width / 2,
      top: placement === 'below' ? rect.bottom + 8 : rect.top - 8,
      placement
    });
  }

  return (
    <DSCard
      aria-describedby={tooltip && tooltipPosition ? tooltipId : undefined}
      className="h-full shadow-[0_8px_32px_oklch(0.2101_0.0318_264.66_/_0.06)]"
      onBlur={() => setTooltipPosition(null)}
      onFocus={(event) => showTooltip(event.currentTarget)}
      onMouseEnter={(event) => showTooltip(event.currentTarget)}
      onMouseLeave={() => setTooltipPosition(null)}
      tabIndex={tooltip ? 0 : undefined}
    >
      <div className="flex h-full flex-col gap-2">
        <div className="flex items-start justify-between gap-3">
          <DSTypography as="p" className="pt-0.5 text-[13px] font-medium leading-snug text-gray-5">
            {label}
          </DSTypography>
          <div
            aria-hidden
            className={`inline-flex size-10 shrink-0 items-center justify-center rounded-[12px] ${toneIconClass[tone]}`}
          >
            {icon}
          </div>
        </div>

        <div className="mt-auto flex min-w-0 flex-col gap-0.5">
          <DSTypography
            as="p"
            className="truncate text-[36px] font-semibold leading-none tracking-tight text-dark-3"
          >
            {value}
          </DSTypography>
          {delta ? (
            <DSTypography as="p" className={`text-[13px] font-medium ${toneDeltaClass[tone]}`}>
              {delta}
            </DSTypography>
          ) : (
            <span aria-hidden className="h-[18px]" />
          )}
        </div>
      </div>

      {tooltip && tooltipPosition && typeof document !== 'undefined'
        ? createPortal(
            <span
              className={[
                'pointer-events-none fixed z-[70] max-w-[260px] -translate-x-1/2 rounded-[12px] bg-brand-primary-3 px-3 py-2 text-left text-[12px] font-normal leading-[16px] tracking-[0px] text-body-light-mode-1 shadow-[0_8px_24px_rgba(0,0,0,0.12)]',
                tooltipPosition.placement === 'above' ? '-translate-y-full' : undefined
              ]
                .filter(Boolean)
                .join(' ')}
              id={tooltipId}
              role="tooltip"
              style={{ left: tooltipPosition.left, top: tooltipPosition.top }}
            >
              {tooltip}
            </span>,
            document.body
          )
        : null}
    </DSCard>
  );
}
